import {
  createContext,
  useState,
  useContext,
  useEffect,
  PropsWithChildren,
} from "react";
import { getAuthorization } from "../api/api";
import { usePrivy, User } from "@privy-io/react-auth";

export const Role = {
  SuperAdmin: "super_admin",
  TenantAdmin: "tenant_admin",
  Creator: "creator",
} as const;

export type Role = (typeof Role)[keyof typeof Role];

type AuthContext = {
  currentUser: User | null;
  userAuthorization: {
    qstarlabs_tenant: string;
    qs_username: string;
    default_persona: string;
    role: Role;
  };
  forceRefresh: boolean;
  setForceRefresh: (value: boolean) => void;
};

const AuthContext = createContext<AuthContext | null>(null);

/** If possible, use {@link useAuth} for a higher-level API. */
export const useAuthContext = () => {
  return useContext(AuthContext)!;
};

const AuthorizationProvider = ({ children }: PropsWithChildren) => {
  const {
    user: currentUser,
    ready,
    authenticated,
    getAccessToken,
  } = usePrivy();
  const [userAuthorization, setUserAuthorization] = useState<any>(null);
  const [forceRefresh, setForceRefresh] = useState(false);

  useEffect(() => {
    const setClaims = async () => {
      const claims = await getAuthorization();
      setUserAuthorization(claims);
    };

    if (!ready) {
      return;
    }
    if (!authenticated) {
      getAccessToken();
      setUserAuthorization(null);
    }
    if (currentUser) {
      setClaims();
    }
  }, [currentUser, authenticated, ready, forceRefresh, getAccessToken]);

  const value = {
    currentUser,
    userAuthorization,
    forceRefresh,
    setForceRefresh,
  } satisfies AuthContext;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthorizationProvider;
